import { distinctUntilKeyChanged, map, switchMap, take, tap } from "rxjs/operators";
import { Injectable, signal } from "@angular/core";
import { Observable, BehaviorSubject, Subject, of, lastValueFrom, firstValueFrom } from "rxjs";
import { Project } from "../../models/project";
import { JobDetail } from "../../models/jobDetail";
import { PlotData } from "../../models/plotData";
import { PlotDetail } from "../../models/plotDetail";
import { SliceType } from "../../models/sliceType";
import { CacheService } from "../../shared/services/cache.service";
import { ProjectService } from "./project.service";
import { Cost } from "../../models/cost";
import { Coordinate, LogMarkers } from "../../models/coordinate";
import { ProjectDialog } from "../../models/projectDialog";
import { JobDetailDialog } from "../../models/jobDetailDialog";
import { JobStatusDialog } from "../../models/jobStatusDialog";
import { ShotOverlayDto } from "../../models/shotOverlayDto";
import { ModelType } from "../../models/modelType";
import { ShotOverlayType } from "../../models/shotOverlayType";
import { Runfile } from "../../models/runfileType";
import { Field } from "../../models/field";
import { G_JobHorizon } from "../../models/generalizedHorizon";
import { JobLog, LogEntry, SummaryLogEntry } from "../../models/jobLog";
import { PaginatedResponse, Paginator } from "./paginator";
import { S3BucketInterface } from "../../models/s3-bucket.model";

interface SonicAPIDTO {
  log: Array<Coordinate>,
  markers: Array<LogMarkers>
}

function CompareJobDetail(a: JobDetail, b: JobDetail) {
  if (a == null || b == null) return false;
  let keys = Object.keys(a);
  for (let key of keys) {
    if (a[key] != b[key]) {
      return false;
    }
  }
  return true;
}

declare const Buffer;
@Injectable({
  providedIn: "root",
})
export class CachedProjectService {
  private _currentJob: BehaviorSubject<JobDetail> =
    new BehaviorSubject<JobDetail>(null);
  public readonly currentJob: Observable<JobDetail> =
    this._currentJob.asObservable();

  private _currentProject: BehaviorSubject<Project> =
    new BehaviorSubject<Project>(null);
  public readonly currentProject: Observable<Project> =
    this._currentProject.asObservable();

  private _fields: BehaviorSubject<Field[]> = new BehaviorSubject<Field[]>([])
  public readonly fields: Observable<Field[]> = this._fields.asObservable();

  // lets pages that delete a job (e.g. runfile, files) notify the project shell
  // so it can advance to the next job instead of navigating away
  private _jobDeleted: Subject<string> = new Subject<string>();
  public readonly jobDeleted: Observable<string> = this._jobDeleted.asObservable();

  // Maintains previously selected definition
  private _selectedGlossaryParameter = signal<{ parameter: string }>({ parameter: "" });
  public readonly selectedGlossaryParameter = this._selectedGlossaryParameter.asReadonly();

  setSelectedGlossaryParameter(parameter: string) {
    this._selectedGlossaryParameter.set({ parameter });
  }

  notifyJobDeleted(jobId: string) {
    this._jobDeleted.next(jobId);
  }

  constructor(
    private projectService: ProjectService,
    private cacheService: CacheService
  ) {
    this.projectService.getAllFields().pipe(take(1)).subscribe({
      next: (res) => {
        this._fields.next(res.fields)
      }, error(err) {
        console.error("error getting fields", err)
      },
    })
  }


  refreshFields(): void {
    this.projectService
      .getAllFields()
      .pipe(map((res) => res.fields))
      .subscribe((fields) => {
        this._fields.next(fields);
      });
  }

  setCurrentProject(project: Project) { 
    this._currentProject.next(project);
  }

  async refreshCurrentProject(){
    const project = await firstValueFrom(this._currentProject)
    const refreshedProject = await firstValueFrom(this.projectService.getProjectById(project.id))
    
    this._currentProject.next(refreshedProject)
    return refreshedProject
  }

  clearProjectCachedById(project_id: string) {
    this.cacheService.delete("project_" + project_id);
  }

  getAllProjects(): Observable<{ id: string; name: string; description: string; field?: string; }[]>{
    return this.projectService.getAllProjects()
  }

  getProjectById(id: string, forceRefresh: boolean = false): Observable<Project> {
    return this.cacheService.get(
      "project_" + id,
      this.projectService.getProjectById(id),
      null, forceRefresh
    );
  }

  getProjectTable(id: string, rebuild = false, ids = []): Observable<any> {
    return this.cacheService.get(
      `project_table_${rebuild}_${ids}` + id,
      this.projectService.getProjectTable(id, rebuild, ids)
    );
  }
  // does not use cache
  _getProjectTable(id: string, rebuild = false, ids = []): Observable<any> {
    return this.projectService.getProjectTable(id, rebuild, ids);
  }
  getParameterTable(
    id: string,
    rebuild = false,
    ids = [],
    forceRefresh: boolean = false
  ): Observable<any> {
    return this.cacheService.get(
      `parameter_table_${rebuild}_${ids}` + id,
      this.projectService.getParameterTable(id, rebuild, ids),
      300000,
      forceRefresh
    );
  }

  getParameterDefinition(parameter: string) {
    return this.cacheService.get(
      `parameter-glossary/${JSON.stringify(parameter)}`,
      this.projectService.getParameterDefinition(parameter),
    );
  }

  getParameterList(): Observable<any[]> {
    return this.cacheService.get(
      `parameter-glossary`,
      this.projectService.getParameterList().pipe(map((res: any) => res.results)),
    );
  }

  // Definition edits are reflected correctly
  clearParameterGlossaryCache(parameter: string) {
    this.cacheService.delete(`parameter-glossary`);
    this.cacheService.delete(`parameter-glossary/${JSON.stringify(parameter)}`);
  }

  getProjectNetwork(id: string, FM = false, ids = []): Observable<any> {
    return this.cacheService.get(
      `project_network_${FM}` + id,
      this.projectService.getProjectNetwork(id, FM)
    );
  }
  getProjectFlowchart(
    id: string,
    FM = false,
    forceRefresh = false
  ): Observable<any> {
    return this.cacheService.get(
      `project_network_v2_${FM}` + id,
      this.projectService.getProjectFlowchart(id, FM),
      null,
      forceRefresh
    );
  }
  // does not use cache
  _getParameterTable(id: string, rebuild = false, ids = []): Observable<any> {
    return this.projectService.getParameterTable(id, rebuild, ids);
  }

  getProjectLevelEnvVars(
    id: string,
    rebuild = false,
    ids = [],
    forceRefresh: boolean = false
  ): Observable<any> {
    return this.cacheService.get(
      `env_vars_table_${rebuild}_${ids}` + id,
      this.projectService.getProjectLevelEnvVars(id, rebuild, ids),
      300000,
      forceRefresh
    );
  }

  // does not use cache
  _getProjectLevelEnvVars(
    id: string,
    rebuild = false,
    ids = []
  ): Observable<any> {
    return this.projectService.getProjectLevelEnvVars(id, rebuild, ids);
  }

  getProjectTableCSV(id: string): Observable<any> {
    return this.projectService.getProjectTableCSV(id);
  }

  // does not use cache
  _getProjectTableCSV(id: string): Observable<any> {
    return this.projectService.getProjectTableCSV(id);
  }


  getTraceSourceData(
    job_id: string,
    param_string: string,
    forceRefresh: boolean = false
  ) {
    return this.cacheService.get(
      `job_${job_id}-params_${param_string}`,
      this.projectService.getTraceSourceData(job_id, param_string),
      null,
      forceRefresh
    );
  }

  getWiggleSourceData(job_id: string, params: { [key: string]: string | number }) {
    return this.cacheService.get(
      `job_${job_id}-params_${JSON.stringify(params)}`,
      this.projectService.getWiggleSourceData(job_id, params),
    );
  }

  getJobDetail(
    id: string,
    forceRefresh: boolean = false
  ): Observable<JobDetail> {
    return this.cacheService
      .get(
        "project_" + id,
        this.projectService.getJobDetail(id),
        null,
        forceRefresh,
      )
      .pipe(
        switchMap((job: JobDetail) => {
          // run the setting current project ONLY if its actually a different project
          const currentProjId = this._currentProject.getValue()?.id;
          if (job.projectId !== currentProjId){
            return this.getProjectById(job.projectId).pipe(
              take(1),
              tap((project) => this.setCurrentProject(project)),
              map(() => job),
            );
          }else{
            return of(job)
          }
        }),
        tap((d: JobDetail) => {
          let currentJob = this._currentJob.getValue();
          if (!CompareJobDetail(currentJob, d)) {
            console.warn("setting current job");
            this._currentJob.next(d);
          }
        }),
      );
  }

  _getJobDetail(id: string, forceRefresh: boolean = false): Observable<JobDetail> {
    // same as above, don't set current job, not intended to use with subscribe.
    return this.cacheService
      .get(
        "project_" + id,
        this.projectService.getJobDetail(id),
        null,
        forceRefresh
      )
  }

  _getProjectDatatable(projectId: string): Observable<any> {
    return this.projectService.getProjectDatatable(projectId);
  }

  _updateProjectDatatable(projectId: string, body: object): Observable<any> {
    return this.projectService.updateProjectDatatable(projectId, body);
  }

  _deleteProjectDatatableRecord(
    projectId: string,
    body: object
  ): Observable<any> {
    return this.projectService.deleteProjectDatatableRecord(projectId, body);
  }

  _rebuildProjectDatatable(projectId: string, body: object): Observable<any> {
    return this.projectService.rebuildProjectDatatable(projectId, body);
  }

  _getProjectModelTable(projectId: string): Observable<any> {
    return this.projectService.getModelTableByProject(projectId);
  }

  _rebuildProjectModelTable(projectId: string): Observable<any> {
    return this.projectService.rebuildModelTable(projectId);
  }

  _updateModeltable(projectId: string, body: object): Observable<any> {
    return this.projectService.updateModeltable(projectId, body);
  }

  _getProjectJobs(projectId: string, body: object): Observable<any> {
    return this.projectService.getProjectJobs(projectId, body);
  }

  getJobIterationsCompleted(id: string): Observable<number> {
    return this.cacheService.get(
      "project_" + id + "_iterationsCompleted",
      this.projectService.getJobIterationsCompleted(id)
    );
  }

  getJobMeta(id: string): Observable<any> {
    return this.cacheService.get(
      "project_" + id + "_meta",
      this.projectService.getJobMeta(id)
    );
  }

  getRunfile(id: string, iteration: number = 0): Observable<any> {
    return this.cacheService.get(
      `project_${id}_runfile+${iteration}`,
      this.projectService.getRunfile(id, iteration)
    );
  }

  getRunfileObject(id: string, keywords: string[] = null): Observable<any> {
    return this.cacheService.get(
      `project_${id}_runfile_object_${keywords.join(",")}`,
      this.projectService.getRunfileObject(id, keywords)
    );
  }

  getPlotData(id: string): Observable<PlotData> {
    return this.cacheService.get(
      "project_" + id + "_plot_data",
      this.projectService.getPlotData(id)
    );
  }

  getMultipleJobData(jobIds, forceRefresh: boolean = false): Observable<any> {
    let idString = jobIds.join("_");
    let hashedString = this._hashString(idString);
    return this.cacheService.get(
      "job_group_" + hashedString,
      this.projectService.getMultipleJobs(jobIds, forceRefresh),
      null, forceRefresh
    );
  }

  getModelTypes(jobId: string, iteration: number): Observable<ModelType[]> {
    return this.cacheService.get(
      "getModelTypes_" + jobId + "_" + iteration,
      this.projectService.getModelTypes(jobId, iteration)
    );
  }

  getSlice(
    id: string,
    modelType: string,
    iteration: number,
    sliceType: SliceType,
    sliceNumber: number,
    is2D: boolean,
    palette: string,
    minVelocity: number = null,
    maxVelocity: number = null,
    shotNumber: number = null,
    fwd: number = null,
    vpRef: number = null,
    agcWindow: number = null,
    timeSlice: number = null
  ): Observable<PlotDetail> {
    return this.cacheService.get(
      `slice_${id}_${modelType}_${iteration}_${agcWindow}_${timeSlice}_${sliceType}_${sliceNumber}_${is2D}_${palette}_${minVelocity}_${maxVelocity}_${shotNumber}_${fwd}_${vpRef}`,
      this.projectService.getSlice(
        id,
        modelType,
        iteration,
        sliceType,
        sliceNumber,
        is2D,
        palette,
        minVelocity,
        maxVelocity,
        shotNumber,
        fwd,
        vpRef,
        agcWindow,
        timeSlice
      )
    );
  }

  getSliceArray(
    url: string,
    width: number,
    height: number,
    min: number,
    max: number
  ): Observable<Array<Array<number>>> {
    return this.projectService.getSliceArray(url, width, height, min, max);
  }

  getRanges(
    id: string,
    modelType: string,
    iteration: number,
    sliceParams: object,
    shot: number = null,
    timeSlice: number = null
  ) {
    return this.cacheService.get(
      `getRanges_${id}_${modelType}_${iteration}_${JSON.stringify(
        sliceParams
      )}_${shot}_${timeSlice}`,
      this.projectService.getRanges(
        id,
        modelType,
        iteration,
        sliceParams,
        shot,
        timeSlice
      )
    );
  }

  getHorizonTypes(id: string): Observable<G_JobHorizon[]> {
    return this.cacheService.get<G_JobHorizon[]>(
      `hasHorizon_${id}`,
      this.projectService.getHorizonTypes(id)
    );
  }

  getProjectHorizonById(
    horizonId: string,
    sliceNumber: number,
    sliceDim: number
  ): Observable<any> {
    return this.cacheService.get(
      `project_horizon_${horizonId}_${sliceNumber}_${sliceDim}`,
      this.projectService.getProjectHorizonById(
        horizonId,
        sliceNumber,
        sliceDim
      )
    );
  }

  getHorizon(
    id: string,
    sliceNumber: number,
    sliceType: SliceType,
    horizonName: string
  ): Observable<number[]> {
    return this.cacheService.get(
      `getHorizon_${id}_${sliceNumber}_${sliceType}_${horizonName}`,
      this.projectService.getHorizon(id, sliceNumber, sliceType, horizonName)
    );
  }

  
  getSonicLog(id: string, frequency?: number): Observable<SonicAPIDTO> {
    return this.cacheService.get(
      `sonicLog_${id}_frequency${frequency}`,
      this.projectService.getSonicLog(id, frequency)
    );
  }

  getCosts(
    id: string,
    totalIterations: number,
    switched: boolean = false
  ): Observable<Cost> {
    return this.cacheService.get(
      `cost_${id}_${totalIterations}_${switched}`,
      this.projectService.getCosts(id, totalIterations, switched)
    );
  }

  getShotOverlay(id: string, iteration: number): Observable<ShotOverlayDto[]> {
    return this.cacheService.get(
      `getShotOverlay_${id}_${iteration}`,
      this.projectService.getShotOverlay(id, iteration)
    );
  }

  getShotOverlayTypes(
    id: string,
    iteration: number = 1,
    model_page: boolean = false
  ): Observable<ShotOverlayType[]> {
    return this.cacheService.get(
      `getShotOverlayTypes_${id}_modelPage_${String(model_page)}`,
      this.projectService.getShotOverlayTypes(id, iteration, model_page)
    );
  }

  getShotsAvailableForModel(
    id: string,
    iteration: number,
    modelType: string = null,
    timeSlice: number = null
  ): Observable<number[]> {
    return this.cacheService.get(
      `getShotsAvailableForModel_${id}_${iteration}_${modelType}`,
      this.projectService.getShotsAvailableForModel(
        id,
        iteration,
        modelType,
        timeSlice
      )
    );
  }

  getTraceFilesByIteration(
    id: string,
    iteration: number
  ): Observable<number[]> {
    return this.cacheService.get(
      `getAvailableTraceFiles_${id}_${iteration}`,
      this.projectService.getTraceFilesByIteration(id, iteration)
    );
  }

  getTraceFilesById(id: string, data: any): Observable<number[]> {
    let contentHash = this._hashString(Object.values(data).join("_"));
    return this.cacheService.get(
      `getAvailableTraceFiles_${id}_${contentHash}`,
      this.projectService.getTraceFilesById(id, data)
    );
  }

  getTraceMeta(id: string, data): Observable<any> {
    let contentHash = this._hashString(Object.values(data).join("_"));
    return this.cacheService.get(
      `getTraceMeta_${id}_${contentHash}`,
      this.projectService.getTraceMeta(id, data)
    );
  }

  getRunfileParameters(
    id: string,
    setDefault: boolean = false
  ): Observable<Runfile> {
    return this.cacheService.get(
      `getRunfileParameters_${id}_${setDefault}`,
      this.projectService.getRunfileParameters(id, setDefault)
    );
  }

  getScreenShots(id: string): Observable<string[]> {
    return this.projectService.getScreenShots(id);
  }

  getAvailableCustomNames(id: string, iteration: number, shot_id: number) {
    return this.cacheService.get(
      `getAvailableCustomNames${id}_${iteration}_${shot_id}`,
      this.projectService.getAvailableCustomNames(id, iteration, shot_id)
    );
  }

  getRcvrData(
    id: string,
    iteration: number,
    shot_id: number,
    fwd: number,
    filterParams: any = {},
    x2IsInline: boolean = false
  ) {
    return this.cacheService.get(
      `getRcvrData_${id}_${iteration}_${shot_id}_${fwd}_${Object.values(
        filterParams
      )}_${x2IsInline}`,
      this.projectService.getRcvrData(
        id,
        iteration,
        shot_id,
        fwd,
        filterParams,
        x2IsInline
      )
    );
  }

  getRcvrData_data(
    id: string,
    iteration: number,
    shot_id: number,
    fwd: number,
    filterParams: any = {},
    x2IsInline: boolean = false
  ) {
    return this.cacheService.get(
      `getRcvrData_data_${id}_${iteration}_${shot_id}_${fwd}_${Object.values(
        filterParams
      )}_${x2IsInline}`,
      this.projectService.getRcvrData_data(
        id,
        iteration,
        shot_id,
        fwd,
        filterParams,
        x2IsInline
      )
    );
  }

  getFilesList(id: string, base_path: string) {
    return this.cacheService.get(
      `getFilesList_${id}_${base_path}`,
      this.projectService.getFilesList(id, base_path)
    );
  }

  getFunctionalSequence(id: string) {
    return this.cacheService.get(
      `getFunctionalSequence_${id}`,
      this.projectService.getFunctionalSequence(id)
    );
  }

  getEnvVariables(id: string) {
    return this.cacheService.get(
      `getEnvVariables_${id}`,
      this.projectService.getEnvVariables(id)
    );
  }

  /**
   * Force refresh environment variables from log files
   */
  refreshEnvVariables(id: string): Observable<any> {
    this.cacheService.delete(`getEnvVariables_${id}`);

    return this.projectService.refreshEnvVariables(id);
  }

  createProject(data: ProjectDialog): Observable<any> {
    this.clearProjectsCache();
    return this.projectService.createProject(data);
  }

  updateProjectDetails(data: ProjectDialog): Observable<any> {
    return this.projectService.updateProjectDetails(data);
  }

  deleteJob(id: string): Observable<any> {
    return this.projectService.deleteJob(id);
  }

  deleteSearchGroup(id: string): Observable<any> {
    return this.projectService.deleteSearchGroup(id);
  }

  deleteScreenshot(id: string): Observable<any> {
    return this.projectService.deleteScreenshot(id);
  }

  updateJobDetails(data: JobDetailDialog): Observable<any> {
    return this.projectService.updateJobDetails(data);
  }

  updateJobEpicModComments(id: string, epicmod_comments): Observable<any> {
    return this.projectService.updateJobEpicModComments(id, epicmod_comments);
  }

  refreshJobDetails(job_id: string): Observable<any> {
    this.cacheService.delete("project_" + job_id);
    return this.projectService.refreshJobDetails(job_id);
  }

  uploadScreenshots(id: string, data: any): Observable<any> {
    return this.projectService.uploadScreenshots(id, data);
  }

  getAllProjectsCosts(): Observable<any> {
    return this.projectService.getAllProjectsCosts();
  }

  checkForNewIterations(job_id: string): Observable<any> {
    return this.projectService.checkForNewIterations(job_id);
  }

  createJobs(project_id: string, job_entries: any[]): Observable<any> {
    return this.projectService.createJobs(project_id, job_entries);
  }

  clearJobDetailCache(ids: string[]) {
    ids.forEach((id) => this.cacheService.delete(`project_${id}`));
  }
  clearJobDetail() {
    this._currentJob.next(null);
  }

  clearProjectsCache() {
    this.cacheService.delete("projects");
  }

  compareModels(data: any): Observable<any> {
    // Not caching model comparison results as they are likely to change frequently
    return this.projectService.compareModels(data);
  }

  getJobProject(id: string, forceRefresh: boolean = false): Observable<Project> {
    return this.projectService.getJobProject(id, forceRefresh);
  }

  getJobOutputErrors(job_id: string, filename: string): Observable<PaginatedResponse<LogEntry>>{
    return this.cacheService.get(
      `job_logs_errors_${job_id}_${filename}`,
      this.projectService.getJobOutputErrors(job_id, filename));
  }

  getJobOutputWarnings(job_id: string, filename: string): Observable<PaginatedResponse<LogEntry>>{
    return this.cacheService.get(
      `job_logs_warnings_${job_id}_${filename}`,
      this.projectService.getJobOutputWarnings(job_id, filename));
  }

  getJobOutputWarningsSummary(job_id: string, filename: string): Observable<SummaryLogEntry[]>{
    return this.cacheService.get(
      `job_logs_warning_summary_${job_id}_${filename}`,
      this.projectService.getJobOutputWarningsSummary(job_id, filename));
  }
  getJobOutputErrorsSummary(job_id: string, filename: string): Observable<SummaryLogEntry[]>{
    return this.cacheService.get(
      `job_logs_errors_summary_${job_id}_${filename}`,
      this.projectService.getJobOutputErrorsSummary(job_id, filename));
  }
  getJobOutputLogFiles(job_id: string): Observable<string[]>{
    return this.cacheService.get(
      `job_logs_files_${job_id}`,
      this.projectService.getJobOutputLogFiles(job_id));
  }
  createPaginator<T>(initial$: Observable<PaginatedResponse<T>>): Paginator<T> {
    return new Paginator(initial$.pipe(take(1)), (url) => this.projectService.getResponse(url));
  }

  getJobTemplate(job_id: string): Observable<any>{
    return this.cacheService.get(
      `job_template_${job_id}`,
      this.projectService.getJobTemplate(job_id));
  }
  getJobConfig(id: string): Observable<any> {
    return this.cacheService.get(
      `job_config_${id}`,
      this.projectService.getJobConfig(id)
    );
  }

  getRunfileFromPath(id: string, path: string): Observable<{path: string, content: string}> {
    return this.cacheService.get(
      `runfile_${path}`,
      this.projectService.getRunfileFromPath(id, path)
    );
  }

  getJsonDataFromPath(path: string): Observable<any> {
    return this.projectService.getJsonDataFromPath(path);
  }

  getJobEnvSh(id: string): Observable<{path: string, content: string}> {
    return this.cacheService.get(
      `job_env_sh_${id}`,
      this.projectService.getJobEnvSh(id)
    );
  }

  submitJob(projectId: string, data: any): Observable<any> {
    return this.projectService.submitJob(projectId, data);
  }

  prepJob(projectId: string, data: any): Observable<any> {
    return this.projectService.prepJob(projectId, data);
  }

  getS3Buckets(): Observable<S3BucketInterface[]>{
    return this.cacheService.get(
      's3-buckets',
      this.projectService.getS3Buckets()
    )
  }

  private _hashString(string) {
    // Source: https://werxltd.com/wp/2010/05/13/javascript-implementation-of-javas-string-hashcode-method/
    var hash = 0,
      i,
      chr;
    for (i = 0; i < string.length; i++) {
      chr = string.charCodeAt(i);
      hash = (hash << 5) - hash + chr;
      hash |= 0; // Convert to 32bit integer
    }
    return hash;
  }
}
